import React, { useState } from 'react';
import './LoreDisplay.css';
import audioManager from '../../../utils/filemanager/AudioManager';
import accountStore from '../../../components/account/AccountStore';
import PlayerService from '../../../services/PlayerService';
import LoreService from '../../../services/LoreService';
import { useLore } from './LoreProvider';
import { useNotification } from '../../../context/NotificationContext';

const LoreSolverModal = ({ loreContent, onClose, onSolve }) => {
  const { refreshProgress } = useLore();
  const { showNotification } = useNotification();
  const [solution, setSolution] = useState('');
  const [feedback, setFeedback] = useState(null); // 'success', 'error', or null
  const [successData, setSuccessData] = useState(null); // for qubit reward display
  const [isSubmitting, setIsSubmitting] = useState(false); // Guard to prevent spam submission

  const handleSubmit = async () => {
    // Guard: Prevent empty submissions and spam clicking
    if (!solution.trim() || isSubmitting) return;

    // Set guard immediately to prevent duplicate submissions
    setIsSubmitting(true);

    // Check against valid answers (handle both string and array formats)
    const validAnswers = typeof loreContent.validAnswers === 'string'
      ? [loreContent.validAnswers]
      : Array.isArray(loreContent.validAnswers)
        ? loreContent.validAnswers
        : [];

    const isCorrect = validAnswers.some(
      answer => answer.toLowerCase() === solution.trim().toLowerCase()
    );

    setFeedback(isCorrect ? 'success' : 'error');

    if (isCorrect) {
      // Play success sound
      audioManager.playSuccess();
      
      // Calculate qubit reward (base reward + bonus for tier)
      const baseQubits = 50;
      const tierBonus = 25;
      const tier = parseInt(loreContent.tier) || 1;
      const totalQubits = parseInt(baseQubits + (tierBonus * tier));
      
      // Get current user data
      const profile = accountStore.getProfile();
      if (profile?.playerId) {
        try {
          // Update qubits
          if (!isNaN(totalQubits)) {
            await PlayerService.updatePlayerData(
              profile.playerId,
              { qubits: totalQubits },
              { increment: true }
            );
          } else {
            console.error('Invalid qubit amount:', totalQubits);
          }

          // Update lore progress
          // Extract the base ID from the full ID (e.g., "mindfabric" from "tier_1_mindfabric")
          const [tier, ...rest] = loreContent.id.split('_');
          const baseId = rest.join('_');
          
          
          
          await LoreService.updateLoreProgress(
            profile.playerId,
            loreContent.id,
            {
              completedAt: new Date().toISOString(),
              solution: solution,
              tier: tier,
              baseId: baseId
            }
          );

          // Refresh progress to update the display
          await refreshProgress();

          // Call onSolve immediately with the updated data
          onSolve({
            solution,
            completedAt: new Date().toISOString(),
            qubitsEarned: totalQubits
          });
        } catch (error) {
          console.error('Error updating lore completion:', error);
        }
      }
      
      // Show reward popup
      setSuccessData({ qubits: totalQubits });
      // Show notification using global context
      showNotification("Return to console-access and _resolve the packet");
      // Close the lore modal
      onClose();
    } else {
      // Play fail sound
      audioManager.playFail();

      // Clear input on wrong answer after a short delay and reset guard
      setTimeout(() => {
        setSolution('');
        setFeedback(null);
        setIsSubmitting(false); // Reset guard to allow retry
      }, 2000);
    }
  };

  return (
    <>
      <div className="puzzle-overlay">
        <div className="puzzle-container lore-solver">
          <h3>{loreContent.prompt || "ENCRYPTED MESSAGE"}</h3>
          
          <div className="cipher-content">
            <div className="encrypted-content">
              <pre>{loreContent.message}</pre>
              {loreContent.expanded && (
                <pre className="expanded-content">{loreContent.expanded}</pre>
              )}
            </div>

            {loreContent.hint && (
              <div className="hint-content">
                <pre style={{ color: '#FFD700' }}>{loreContent.hint}</pre>
              </div>
            )}

            <div className="input-group">
              <input
                type="text"
                value={solution}
                onChange={(e) => setSolution(e.target.value)}
                placeholder="Enter solution..."
                className={`quantum-input ${feedback ? `feedback-${feedback}` : ''}`}
              />
              {feedback && (
                <div className={`cipher-feedback ${feedback}`}>
                  {feedback === 'success' ? 'CORRECT' : 'INCORRECT - TRY AGAIN'}
                </div>
              )}
            </div>
          </div>

          <div className="cipher-actions">
            <button
              className="lore-display-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PROCESSING...' : 'SUBMIT'}
            </button>
            <button
              className="lore-display-button secondary"
              onClick={onClose}
            >
              CLOSE
            </button>
          </div>
        </div>
        {successData && (
          <div className="reward-popup">
            +{successData.qubits} QUBITS
          </div>
        )}
      </div>
    </>
  );
};

export default LoreSolverModal;